import React, { useState, useEffect } from 'react';
import { Archive, Search, MapPin, Package, DollarSign, Calendar, RefreshCw, RotateCcw, Clock, User, CheckCircle } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminArchives = () => {
  const { getAuthHeader, userType } = useAuth();
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  const isFullAdmin = userType === 'admin';

  useEffect(() => {
    fetchArchivedJobs();
  }, []);

  const fetchArchivedJobs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/jobs/archived`, getAuthHeader());
      setArchivedJobs(response.data);
    } catch (error) {
      toast.error('Failed to fetch archived jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (jobId, jobTitle) => {
    if (!window.confirm(`Restore "${jobTitle}" from archives?`)) return;
    
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/unarchive`, {}, getAuthHeader());
      toast.success('Job restored successfully');
      fetchArchivedJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore job');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredJobs = archivedJobs.filter(job =>
    job.title?.toLowerCase().includes(search.toLowerCase()) ||
    job.client?.toLowerCase().includes(search.toLowerCase()) ||
    job.location?.toLowerCase().includes(search.toLowerCase())
  );

  // Group jobs by month
  const groupedJobs = filteredJobs.reduce((groups, job) => {
    const date = new Date(job.archived_at || job.completed_at);
    const monthYear = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    if (!groups[monthYear]) groups[monthYear] = [];
    groups[monthYear].push(job);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-archives-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Archive className="h-8 w-8 text-gray-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Archives</h1>
            <p className="text-gray-500 text-sm">Completed and archived jobs for record keeping</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-gray-600">
            {archivedJobs.length} archived job{archivedJobs.length !== 1 ? 's' : ''}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchArchivedJobs}
            className="flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Search archived jobs by title, client, or location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-archives"
          />
        </div>
      </Card>

      {/* Archived Jobs List */}
      {filteredJobs.length === 0 ? (
        <Card className="p-12 text-center">
          <Archive className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Archived Jobs</h3>
          <p className="text-gray-500">
            {search ? 'No jobs match your search criteria' : 'Completed and archived jobs will appear here'}
          </p>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedJobs).map(([monthYear, jobs]) => (
            <div key={monthYear}>
              <h3 className="text-sm font-semibold text-gray-500 mb-3 flex items-center gap-2">
                <Calendar size={14} />
                {monthYear}
                <Badge variant="outline" className="ml-2">{jobs.length}</Badge>
              </h3>
              
              <div className="space-y-3">
                {jobs.map((job) => (
                  <Card 
                    key={job.id} 
                    className="p-4 hover:shadow-md transition-shadow"
                    data-testid={`archived-job-${job.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
                            <Archive className="text-gray-500" size={20} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-gray-900">
                                {job.job_number && <span className="text-blue-600 mr-1">#{job.job_number}</span>}
                                {job.title}
                              </h4>
                              {job.status === 'completed' ? (
                                <Badge className="bg-purple-100 text-purple-700">
                                  <CheckCircle size={12} className="mr-1" />
                                  Completed
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-600">
                                  <Archive size={12} className="mr-1" />
                                  Archived
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{job.client}</p>
                            
                            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <MapPin size={14} className="text-gray-400" />
                                {job.location}
                              </span>
                              <span className="flex items-center gap-1">
                                <Package size={14} className="text-gray-400" />
                                {job.deliveries} deliveries
                              </span>
                              <span className="flex items-center gap-1">
                                <DollarSign size={14} className="text-gray-400" />
                                ${job.total_pay?.toFixed(2)}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                Completed: {formatDate(job.completed_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Archive size={12} />
                                Archived: {formatDate(job.archived_at)}
                              </span>
                              {job.archived_by && (
                                <span className="flex items-center gap-1">
                                  <User size={12} />
                                  By: {job.archived_by}
                                  {job.auto_archived && (
                                    <Badge variant="outline" className="ml-1 text-xs py-0">Auto</Badge>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {isFullAdmin && job.status === 'archived' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUnarchive(job.id, job.title)}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`unarchive-${job.id}`}
                        >
                          <RotateCcw size={16} />
                          Restore
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Footer */}
      <Card className="mt-6 p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
            <Archive size={16} className="text-blue-600" />
          </div>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">About Archives</p>
            <p className="text-blue-600">
              Completed jobs automatically appear here. Jobs are auto-archived 180 days after completion. 
              Archived jobs are kept for record-keeping and reporting purposes.
              {isFullAdmin ? ' Administrators can restore archived jobs if needed.' : ''}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminArchives;
